import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import NavigationBar from './NavigationBar';
import EventNavigation from './EventNavigation';
import LoadingSpinner from './LoadingSpinner';
import { MapPin, Clock, Users, ChevronDown, ChevronUp, X } from 'lucide-react';
import './EventHome.css';
import './EventAgenda.css';

const EventAgenda = () => {
    const { eventNameId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [agendaLoading, setAgendaLoading] = useState(true);
    const [error, setError] = useState('');
    const [agendaData, setAgendaData] = useState(null);
    const [speakers, setSpeakers] = useState([]);
    const [selectedDayId, setSelectedDayId] = useState(null);
    const [selectedTracks, setSelectedTracks] = useState(new Set());
    const [expandedSessions, setExpandedSessions] = useState(new Set());
    const [selectedSpeaker, setSelectedSpeaker] = useState(null);

    useEffect(() => {
        if (eventNameId) {
            localStorage.setItem('last_event', eventNameId);
            fetchEventData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventNameId]);

    const fetchEventData = async () => {
        try {
            setLoading(true);
            const response = await apiService.getEventByNameId(eventNameId);
            setEvent(response.data);
            
            // Fetch agenda data using ic_event_name_id
            if (response.data?.ic_event_name_id) {
                fetchAgendaData(response.data.ic_event_name_id);
            } else {
                setAgendaLoading(false);
            }
        } catch (err) {
            console.error('Error fetching event:', err);
            setError('Failed to load event details');
            setAgendaLoading(false);
        } finally {
            setLoading(false);
        }
    };

    // Sessions to exclude from display (using startsWith to handle trailing spaces)
    const excludedSessionTitles = ['Golf Outing', 'Padel Outing'];
    // Days to exclude (by startDate text)
    const excludedDays = ['Thu, Feb 26'];

    // Helper to check if a session title should be excluded
    const shouldExcludeSession = (title) => {
        if (!title) return false;
        const trimmedTitle = title.trim();
        return excludedSessionTitles.some(excluded => 
            trimmedTitle === excluded || trimmedTitle.startsWith(excluded)
        );
    };

    const fetchAgendaData = async (icEventNameId) => {
        try {
            setAgendaLoading(true);
            const response = await apiService.getEventAgenda(icEventNameId);
            
            if (response.data?.success) {
                const agenda = response.data.agenda;
                
                // Filter out excluded days and excluded sessions within each day
                if (agenda?.agendaDays) {
                    agenda.agendaDays = agenda.agendaDays
                        .filter(day => !excludedDays.includes(day.startDate))
                        .map(day => ({
                            ...day,
                            sessions: day.sessions?.filter(
                                session => !shouldExcludeSession(session.title)
                            ) || []
                        }));
                }
                
                setAgendaData(agenda);
                setSpeakers(response.data.speakers || []);
                
                // Set default selected day (make sure it's not an excluded day)
                const validDays = agenda?.agendaDays || [];
                if (validDays.length > 0) {
                    // Try to use defaultDay if it's still valid
                    const defaultDayStillValid = validDays.some(
                        day => day.agendaDayId === response.data.agenda?.defaultDay
                    );
                    if (defaultDayStillValid) {
                        setSelectedDayId(response.data.agenda.defaultDay);
                    } else {
                        setSelectedDayId(validDays[0].agendaDayId);
                    }
                }
                
                // Initialize all tracks as selected
                if (agenda?.sessionTracks) {
                    const allTracks = new Set(agenda.sessionTracks.map(t => t.trackName));
                    setSelectedTracks(allTracks);
                }
            }
        } catch (err) {
            console.error('Error fetching agenda:', err);
        } finally {
            setAgendaLoading(false);
        }
    };

    // Get current day's sessions
    const currentDay = useMemo(() => {
        if (!agendaData?.agendaDays || !selectedDayId) return null;
        return agendaData.agendaDays.find(day => day.agendaDayId === selectedDayId);
    }, [agendaData, selectedDayId]);

    // Filter sessions by selected tracks
    const filteredSessions = useMemo(() => {
        if (!currentDay?.sessions) return [];
        if (selectedTracks.size === 0) return currentDay.sessions;
        return currentDay.sessions.filter(session => 
            selectedTracks.has(session.trackName)
        );
    }, [currentDay, selectedTracks]);

    // Get unique tracks from current day
    const availableTracks = useMemo(() => {
        if (!agendaData?.sessionTracks) return [];
        return agendaData.sessionTracks;
    }, [agendaData]);

    const toggleTrack = (trackName) => {
        setSelectedTracks(prev => {
            const newSet = new Set(prev);
            if (newSet.has(trackName)) {
                newSet.delete(trackName);
            } else {
                newSet.add(trackName);
            }
            return newSet;
        });
    };

    const toggleSession = (sessionId) => {
        setExpandedSessions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sessionId)) {
                newSet.delete(sessionId);
            } else {
                newSet.add(sessionId);
            }
            return newSet;
        });
    };

    const parseHtmlDescription = (html, trackName = null) => {
        if (!html) return null;
        // Simple HTML stripping for display
        const div = document.createElement('div');
        div.innerHTML = html;
        let text = div.textContent || div.innerText || '';
        
        // For Funds4Charity track, remove "invite only" text from descriptions
        const isFunds4CharityTrack = trackName && 
            trackName.toLowerCase().replace(/\s+/g, '').includes('funds4charity');
        
        if (isFunds4CharityTrack) {
            // Remove variations of "invite only" (case insensitive)
            // Handles: "Invite Only", "invite only", "(invite only)", "- invite only", etc.
            text = text.replace(/[([/\-–—]*\s*invite\s+only\s*[)\]/\-–—]*/gi, '').trim();
            // Clean up any leftover double spaces or punctuation issues
            text = text.replace(/\s{2,}/g, ' ').trim();
        }
        
        return text;
    };

    // eslint-disable-next-line no-unused-vars
    const getSpeakerById = (speakerId) => {
        return speakers.find(s => s.speakerId === speakerId);
    };

    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <LoadingSpinner message="Loading agenda..." />
                    </div>
                </div>
            </>
        );
    }

    if (error || !event) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <div className="error-message">
                            <h2>Error</h2>
                            <p>{error || 'Event not found'}</p>
                            <button onClick={() => navigate('/login')} className="btn btn-primary">
                                Return to Login
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <NavigationBar />
            <div className="event-layout">
                <EventNavigation eventNameId={eventNameId} />
                <div className="event-home-container with-sidebar">
                    <div className="agenda-page">
                        <div className="agenda-header">
                            <h1>Agenda</h1>
                            <p className="agenda-subtitle">
                                Event schedule for {event.Name}
                            </p>
                        </div>
                        
                        {agendaLoading ? (
                            <div className="agenda-loading">
                                <LoadingSpinner message="Loading agenda..." />
                            </div>
                        ) : !agendaData ? (
                            <div className="agenda-empty">
                                <p>No agenda available for this event.</p>
                            </div>
                        ) : (
                            <div className="agenda-content">
                                {/* Day Tabs */}
                                <div className="agenda-day-tabs">
                                    {agendaData.agendaDays.map(day => (
                                        <button
                                            key={day.agendaDayId}
                                            className={`agenda-day-tab ${selectedDayId === day.agendaDayId ? 'active' : ''}`}
                                            onClick={() => setSelectedDayId(day.agendaDayId)}
                                        >
                                            {day.startDate}
                                        </button>
                                    ))}
                                </div>

                                {/* Track Filters */}
                                {availableTracks.length > 0 && (
                                    <div className="agenda-track-filters">
                                        <span className="filter-label">Filter by track:</span>
                                        <div className="track-chips">
                                            {availableTracks.map(track => (
                                                <button
                                                    key={track.trackName}
                                                    className={`track-chip ${selectedTracks.has(track.trackName) ? 'active' : ''}`}
                                                    onClick={() => toggleTrack(track.trackName)}
                                                    style={{
                                                        '--track-color': track.trackColor || '#33415580'
                                                    }}
                                                >
                                                    {track.trackName}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Sessions List */}
                                <div className="agenda-sessions">
                                    {filteredSessions.length === 0 ? (
                                        <div className="no-sessions">
                                            <p>No sessions match the selected filters.</p>
                                        </div>
                                    ) : (
                                        filteredSessions.map(session => {
                                            // Check if session has any expandable content
                                            const parsedDescription = parseHtmlDescription(session.description, session.trackName);
                                            const hasDescription = parsedDescription && parsedDescription.trim().length > 0;
                                            const hasSpeakers = session.speakers && session.speakers.length > 0;
                                            const hasSponsors = session.sponsors && session.sponsors.length > 0;
                                            const hasExpandableContent = hasDescription || hasSpeakers || hasSponsors;

                                            return (
                                            <div 
                                                key={session.sessionId} 
                                                className={`agenda-session-card ${expandedSessions.has(session.sessionId) ? 'expanded' : ''} ${!hasExpandableContent ? 'no-expand' : ''}`}
                                            >
                                                <div 
                                                    className={`session-header ${!hasExpandableContent ? 'no-click' : ''}`}
                                                    onClick={hasExpandableContent ? () => toggleSession(session.sessionId) : undefined}
                                                >
                                                    <div className="session-time-track">
                                                        <div className="session-time">
                                                            <Clock size={14} />
                                                            <span>{session.sessionDates}</span>
                                                        </div>
                                                        <span 
                                                            className="session-track-badge"
                                                            style={{
                                                                backgroundColor: session.trackColor || '#33415580'
                                                            }}
                                                        >
                                                            {session.trackName}
                                                        </span>
                                                    </div>
                                                    <div className="session-main">
                                                        <h3 className="session-title">{session.title}</h3>
                                                        {session.location && (
                                                            <div className="session-location">
                                                                <MapPin size={14} />
                                                                <span>{session.location}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {hasExpandableContent && (
                                                        <button className="session-expand-btn">
                                                            {expandedSessions.has(session.sessionId) ? (
                                                                <ChevronUp size={20} />
                                                            ) : (
                                                                <ChevronDown size={20} />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>

                                                {hasExpandableContent && expandedSessions.has(session.sessionId) && (
                                                    <div className="session-details">
                                                        {hasDescription && (
                                                            <div className="session-description">
                                                                <p>{parsedDescription}</p>
                                                            </div>
                                                        )}

                                                        {hasSpeakers && (
                                                            <div className="session-speakers">
                                                                <h4>
                                                                    <Users size={16} />
                                                                    <span>Speakers</span>
                                                                </h4>
                                                                <div className="speakers-list">
                                                                    {session.speakers.map((speaker, idx) => (
                                                                        <div 
                                                                            key={idx} 
                                                                            className="speaker-mini-card"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setSelectedSpeaker(speaker);
                                                                            }}
                                                                        >
                                                                            {speaker.imageUrl ? (
                                                                                <img 
                                                                                    src={speaker.imageUrl} 
                                                                                    alt={speaker.name}
                                                                                    className="speaker-avatar"
                                                                                />
                                                                            ) : (
                                                                                <div className="speaker-avatar-placeholder">
                                                                                    {speaker.name?.charAt(0)}
                                                                                </div>
                                                                            )}
                                                                            <div className="speaker-info">
                                                                                <span className="speaker-name">{speaker.name}</span>
                                                                                {speaker.jobTitle && (
                                                                                    <span className="speaker-title">{speaker.jobTitle}</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {hasSponsors && (
                                                            <div className="session-sponsors">
                                                                <h4>Sponsors</h4>
                                                                <div className="sponsors-grid">
                                                                    {session.sponsors.map((sponsor, idx) => (
                                                                        <a
                                                                            key={idx}
                                                                            href={sponsor.sponsorURL}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="sponsor-logo-link"
                                                                            title={sponsor.sponsorName}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            {sponsor.imageURL ? (
                                                                                <img 
                                                                                    src={sponsor.imageURL} 
                                                                                    alt={sponsor.sponsorName}
                                                                                    className="sponsor-logo"
                                                                                />
                                                                            ) : (
                                                                                <span className="sponsor-name-text">
                                                                                    {sponsor.sponsorName}
                                                                                </span>
                                                                            )}
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Speaker Modal */}
            {selectedSpeaker && (
                <div className="speaker-modal-overlay" onClick={() => setSelectedSpeaker(null)}>
                    <div className="speaker-modal" onClick={e => e.stopPropagation()}>
                        <button 
                            className="speaker-modal-close"
                            onClick={() => setSelectedSpeaker(null)}
                        >
                            <X size={24} />
                        </button>
                        <div className="speaker-modal-content">
                            <div className="speaker-modal-header">
                                {selectedSpeaker.imageUrl ? (
                                    <img 
                                        src={selectedSpeaker.imageUrl} 
                                        alt={selectedSpeaker.name}
                                        className="speaker-modal-avatar"
                                    />
                                ) : (
                                    <div className="speaker-modal-avatar-placeholder">
                                        {selectedSpeaker.name?.charAt(0)}
                                    </div>
                                )}
                                <div className="speaker-modal-info">
                                    <h2>{selectedSpeaker.name}</h2>
                                    {selectedSpeaker.jobTitle && (
                                        <p className="speaker-modal-title">{selectedSpeaker.jobTitle}</p>
                                    )}
                                    {selectedSpeaker.companyName && (
                                        <p className="speaker-modal-company">{selectedSpeaker.companyName}</p>
                                    )}
                                </div>
                            </div>
                            {selectedSpeaker.biography && (
                                <div className="speaker-modal-bio">
                                    <p>{parseHtmlDescription(selectedSpeaker.biography)}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EventAgenda;
